import { useState, useEffect } from 'react';
import { api } from '../services/api';

interface DashboardStats {
  totalUsers: number;
  totalConversations: number;
  totalMessages: number;
  totalSubscriptions: number;
}

interface RecentUser {
  id: string;
  email: string;
  name: string | null;
  role: string;
  subscriptionTier: string;
  createdAt: string;
}

interface User {
  id: string;
  email: string;
  name: string | null;
  role: string;
  subscriptionTier: string;
  createdAt: string;
  _count: {
    conversations: number;
  };
}

interface InvitationCode {
  id: string;
  code: string;
  planId: string;
  planType: string;
  maxRedemptions: number;
  currentRedemptions: number;
  isActive: boolean;
  createdAt: string;
  description: string | null;
  plan?: {
    name: string;
  };
}

interface CodeStats {
  totalCodes: number;
  activeCodes: number;
  totalRedemptions: number;
}

interface Plan {
  id: string;
  name: string;
  priceMonthly: number | null;
  priceYearly: number | null;
  priceLifetime: number | null;
}

export default function AdminPage() {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'users' | 'codes' | 'subscriptions'>('dashboard');
  const [loading, setLoading] = useState(true);
  
  // Dashboard state
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentUsers, setRecentUsers] = useState<RecentUser[]>([]);
  
  // Users state
  const [users, setUsers] = useState<User[]>([]);
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersSearch, setUsersSearch] = useState('');
  
  // Codes state
  const [codes, setCodes] = useState<InvitationCode[]>([]);
  const [codeStats, setCodeStats] = useState<CodeStats | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [showCreateCode, setShowCreateCode] = useState(false);
  const [newCode, setNewCode] = useState({
    planId: '',
    planType: 'monthly' as 'monthly' | 'yearly' | 'lifetime',
    maxRedemptions: 1,
    expiresAt: '',
    description: '',
  });

  useEffect(() => {
    loadDashboard();
    loadCodes();
    loadPlans();
  }, []);

  useEffect(() => {
    if (activeTab === 'users') {
      loadUsers();
    }
  }, [activeTab, usersPage, usersSearch]);

  const loadDashboard = async () => {
    try {
      const response = await api.get('/admin/stats');
      if (response.data.stats) {
        setStats(response.data.stats);
        setRecentUsers(response.data.recentUsers || []);
      }
    } catch (error) {
      console.error('Failed to load dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await api.get('/admin/users', {
        params: {
          page: usersPage,
          limit: 20,
          search: usersSearch,
        },
      });
      if (response.data.users) {
        setUsers(response.data.users);
        setUsersTotal(response.data.pagination?.total || 0);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const loadCodes = async () => {
    try {
      const [codesRes, statsRes] = await Promise.all([
        api.get('/admin/codes'),
        api.get('/admin/codes/stats'),
      ]);
      if (codesRes.data.data) {
        setCodes(codesRes.data.data);
      }
      if (statsRes.data.data) {
        setCodeStats(statsRes.data.data);
      }
    } catch (error) {
      console.error('Failed to load codes:', error);
    }
  };

  const loadPlans = async () => {
    try {
      const response = await api.get('/admin/codes/plans');
      if (response.data.data) {
        setPlans(response.data.data);
        if (response.data.data.length > 0) {
          setNewCode({ ...newCode, planId: response.data.data[0].id });
        }
      }
    } catch (error) {
      console.error('Failed to load plans:', error);
    }
  };

  const handleCreateCode = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await api.post('/admin/codes', newCode);
      if (response.data.success) {
        setShowCreateCode(false);
        setNewCode({
          planId: plans[0]?.id || '',
          planType: 'monthly',
          maxRedemptions: 1,
          expiresAt: '',
          description: '',
        });
        loadCodes();
      }
    } catch (error) {
      console.error('Failed to create code:', error);
    }
  };

  const handleToggleCode = async (codeId: string, isActive: boolean) => {
    try {
      const endpoint = `/admin/codes/${codeId}/${isActive ? 'deactivate' : 'activate'}`;
      await api.patch(endpoint);
      loadCodes();
    } catch (error) {
      console.error('Failed to toggle code:', error);
    }
  };

  const handleDeleteCode = async (codeId: string) => {
    if (!confirm('Are you sure you want to delete this code?')) return;
    try {
      await api.delete(`/admin/codes/${codeId}`);
      loadCodes();
    } catch (error) {
      console.error('Failed to delete code:', error);
    }
  };

  const handleUpdateUser = async (userId: string, role: string, tier: string) => {
    try {
      await api.patch(`/admin/users/${userId}`, { role, subscriptionTier: tier });
      loadUsers();
    } catch (error) {
      console.error('Failed to update user:', error);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Are you sure you want to delete this user?')) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      loadUsers();
      loadDashboard();
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold text-white mb-6">Admin Dashboard</h1>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-700">
        {[
          { id: 'dashboard', label: 'Dashboard' },
          { id: 'users', label: 'Users' },
          { id: 'codes', label: 'Invitation Codes' },
          { id: 'subscriptions', label: 'Subscriptions' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`px-4 py-2 rounded-t-lg font-medium ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-gray-800'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="card">
              <p className="text-dark-400 text-sm">Total Users</p>
              <p className="text-3xl font-bold text-white">{stats?.totalUsers || 0}</p>
            </div>
            <div className="card">
              <p className="text-dark-400 text-sm">Total Conversations</p>
              <p className="text-3xl font-bold text-white">{stats?.totalConversations || 0}</p>
            </div>
            <div className="card">
              <p className="text-dark-400 text-sm">Total Messages</p>
              <p className="text-3xl font-bold text-white">{stats?.totalMessages || 0}</p>
            </div>
            <div className="card">
              <p className="text-dark-400 text-sm">Active Subscriptions</p>
              <p className="text-3xl font-bold text-white">{stats?.totalSubscriptions || 0}</p>
            </div>
          </div>

          <div className="card">
            <h2 className="text-xl font-semibold text-white mb-4">Recent Users</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-gray-400 text-sm border-b border-gray-700">
                    <th className="pb-2">Email</th>
                    <th className="pb-2">Role</th>
                    <th className="pb-2">Plan</th>
                    <th className="pb-2">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {recentUsers.map((user) => (
                    <tr key={user.id} className="border-b border-gray-800">
                      <td className="py-3 text-white">{user.email}</td>
                      <td className="py-3">
                        <span className={`px-2 py-1 rounded text-xs ${
                          user.role === 'superadmin' ? 'bg-purple-600' : 'bg-gray-600'
                        } text-white`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="py-3 text-gray-300">{user.subscriptionTier}</td>
                      <td className="py-3 text-gray-400">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                  {recentUsers.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-gray-500">
                        No users yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="card">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-white">User Management</h2>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Search users..."
                value={usersSearch}
                onChange={(e) => setUsersSearch(e.target.value)}
                className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-gray-400 text-sm border-b border-gray-700">
                  <th className="pb-2">Email</th>
                  <th className="pb-2">Role</th>
                  <th className="pb-2">Plan</th>
                  <th className="pb-2">Joined</th>
                  <th className="pb-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-gray-800">
                    <td className="py-3 text-white">{user.email}</td>
                    <td className="py-3">
                      <select
                        value={user.role}
                        onChange={(e) => handleUpdateUser(user.id, e.target.value, user.subscriptionTier)}
                        className="bg-gray-800 text-white text-sm rounded px-2 py-1"
                      >
                        <option value="user">User</option>
                        <option value="superadmin">Superadmin</option>
                      </select>
                    </td>
                    <td className="py-3">
                      <select
                        value={user.subscriptionTier}
                        onChange={(e) => handleUpdateUser(user.id, user.role, e.target.value)}
                        className="bg-gray-800 text-white text-sm rounded px-2 py-1"
                      >
                        <option value="free">Free</option>
                        <option value="pro">Pro</option>
                        <option value="team">Team</option>
                      </select>
                    </td>
                    <td className="py-3 text-gray-300">{user._count.conversations}</td>
                    <td className="py-3 text-gray-400">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-3">
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        className="text-red-500 hover:text-red-400 text-sm"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-gray-500">
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {usersTotal > 20 && (
            <div className="flex justify-center gap-2 mt-4">
              <button
                onClick={() => setUsersPage(p => Math.max(1, p - 1))}
                disabled={usersPage === 1}
                className="px-3 py-1 bg-gray-800 text-white rounded disabled:opacity-50"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-gray-400">
                Page {usersPage} of {Math.ceil(usersTotal / 20)}
              </span>
              <button
                onClick={() => setUsersPage(p => p + 1)}
                disabled={usersPage >= Math.ceil(usersTotal / 20)}
                className="px-3 py-1 bg-gray-800 text-white rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {/* Codes Tab */}
      {activeTab === 'codes' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="card">
              <p className="text-dark-400 text-sm">Total Codes</p>
              <p className="text-3xl font-bold text-white">{codeStats?.totalCodes || 0}</p>
            </div>
            <div className="card">
              <p className="text-dark-400 text-sm">Active Codes</p>
              <p className="text-3xl font-bold text-white">{codeStats?.activeCodes || 0}</p>
            </div>
            <div className="card">
              <p className="text-dark-400 text-sm">Total Redemptions</p>
              <p className="text-3xl font-bold text-white">{codeStats?.totalRedemptions || 0}</p>
            </div>
          </div>

          <div className="card">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">Invitation Codes</h2>
              <button
                onClick={() => setShowCreateCode(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Create Code
              </button>
            </div>

            {/* Create Code Modal */}
            {showCreateCode && (
              <div className="mb-6 p-4 bg-gray-800 rounded-lg">
                <h3 className="text-lg font-semibold text-white mb-4">Create Invitation Code</h3>
                <form onSubmit={handleCreateCode}>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">Plan</label>
                      <select
                        value={newCode.planId}
                        onChange={(e) => setNewCode({ ...newCode, planId: e.target.value })}
                        className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
                      >
                        {plans.map((plan) => (
                          <option key={plan.id} value={plan.id}>{plan.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">Plan Type</label>
                      <select
                        value={newCode.planType}
                        onChange={(e) => setNewCode({ ...newCode, planType: e.target.value as any })}
                        className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
                      >
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                        <option value="lifetime">Lifetime</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">Max Redemptions</label>
                      <input
                        type="number"
                        min="1"
                        value={newCode.maxRedemptions}
                        onChange={(e) => setNewCode({ ...newCode, maxRedemptions: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">Expires At (optional)</label>
                      <input
                        type="datetime-local"
                        value={newCode.expiresAt}
                        onChange={(e) => setNewCode({ ...newCode, expiresAt: e.target.value })}
                        className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-gray-400 text-sm mb-1">Description (optional)</label>
                      <input
                        type="text"
                        value={newCode.description}
                        onChange={(e) => setNewCode({ ...newCode, description: e.target.value })}
                        className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
                        placeholder="e.g., Beta testers"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                      Create
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCreateCode(false)}
                      className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-gray-400 text-sm border-b border-gray-700">
                    <th className="pb-2">Code</th>
                    <th className="pb-2">Plan</th>
                    <th className="pb-2">Type</th>
                    <th className="pb-2">Usage</th>
                    <th className="pb-2">Status</th>
                    <th className="pb-2">Created</th>
                    <th className="pb-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {codes.map((code) => (
                    <tr key={code.id} className="border-b border-gray-800">
                      <td className="py-3 text-white font-mono text-sm">{code.code}</td>
                      <td className="py-3 text-gray-300">{code.plan?.name || code.planId}</td>
                      <td className="py-3 text-gray-300 capitalize">{code.planType}</td>
                      <td className="py-3 text-gray-300">
                        {code.currentRedemptions} / {code.maxRedemptions}
                      </td>
                      <td className="py-3">
                        <span className={`px-2 py-1 rounded text-xs ${
                          code.isActive ? 'bg-green-600' : 'bg-red-600'
                        } text-white`}>
                          {code.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-3 text-gray-400">
                        {new Date(code.createdAt).toLocaleDateString()}
                      </td>
                      <td className="py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleToggleCode(code.id, code.isActive)}
                            className="text-blue-500 hover:text-blue-400 text-sm"
                          >
                            {code.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            onClick={() => handleDeleteCode(code.id)}
                            className="text-red-500 hover:text-red-400 text-sm"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {codes.length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-gray-500">
                        No invitation codes yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Subscriptions Tab */}
      {activeTab === 'subscriptions' && (
        <div className="card">
          <h2 className="text-xl font-semibold text-white mb-4">Subscription Management</h2>
          <p className="text-gray-400 mb-4">
            Manage user subscriptions. Users can manage their own subscriptions through the billing page.
            Use the User Management tab to manually update subscription tiers.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-800 p-4 rounded-lg">
              <h3 className="font-semibold text-white mb-2">Free Plan</h3>
              <p className="text-gray-400 text-sm">Default plan for all users</p>
              <p className="text-2xl font-bold text-white mt-2">$0/month</p>
            </div>
            <div className="bg-gray-800 p-4 rounded-lg">
              <h3 className="font-semibold text-white mb-2">Pro Plan</h3>
              <p className="text-gray-400 text-sm">For power users</p>
              <p className="text-2xl font-bold text-white mt-2">$29/month</p>
            </div>
            <div className="bg-gray-800 p-4 rounded-lg">
              <h3 className="font-semibold text-white mb-2">Team Plan</h3>
              <p className="text-gray-400 text-sm">For teams and businesses</p>
              <p className="text-2xl font-bold text-white mt-2">$99/month</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

